import React, { useState, useEffect } from 'react';
import { addVideoLesson, getAllVideos } from '../../services/admin';

const VideoManagement = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    subjectId: '',
    levelId: '',
    videoUrl: '',
    thumbnailUrl: '',
    duration: '',
    topic: ''
  });

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const videosData = await getAllVideos();
        setVideos(videosData);
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await addVideoLesson(formData);
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        subjectId: '',
        levelId: '',
        videoUrl: '',
        thumbnailUrl: '',
        duration: '',
        topic: ''
      });
      setShowAddModal(false);
      
      // Refresh videos list
      const videosData = await getAllVideos();
      setVideos(videosData);
      
      alert('Video lesson added successfully!');
    } catch (error) {
      console.error('Error adding video:', error);
      alert('Failed to add video. Please try again.');
    }
  };

  const extractYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const getYouTubeThumbnail = (url) => {
    const videoId = extractYouTubeId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : '';
  };

  const handleVideoUrlChange = (url) => {
    setFormData({
      ...formData,
      videoUrl: url,
      thumbnailUrl: getYouTubeThumbnail(url)
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="video-management">
        <div className="loading-container">
          <div className="loading-spinner">Loading videos...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="video-management">
      <div className="page-header">
        <h1>Video Lessons Management</h1>
        <p>Add and manage YouTube video lessons</p>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary"
        >
          + Add Video
        </button>
      </div>

      {/* Videos Grid */}
      <div className="videos-grid">
        {videos.map((video) => (
          <div key={video.id} className="video-card">
            <div className="video-thumbnail">
              <img 
                src={video.thumbnailUrl || 'https://via.placeholder.com/320x180?text=Video'} 
                alt={video.title}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/320x180?text=Video';
                }}
              />
              <div className="video-duration">
                {formatDuration(video.duration)}
              </div>
            </div>
            
            <div className="video-info">
              <h3>{video.title}</h3>
              <p className="video-description">{video.description}</p>
              <div className="video-meta">
                <span className="meta-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                    <polyline points="8,7 16,7 16,11 8,11"/>
                    <polyline points="8,13 16,13 16,17 8,17"/>
                  </svg>
                  {video.subjectId}
                </span>
                <span className="meta-item">
                  🎓 {video.levelId?.replace('grade', 'Grade ').replace('form', 'Form ')}
                </span>
                <span className="meta-item">
                  👁️ {video.viewCount || 0} views
                </span>
              </div>
            </div>

            <div className="video-actions">
              <a
                href={video.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-secondary"
              >
                Watch
              </a>
              <button className="btn btn-sm btn-primary">
                Edit
              </button>
              <button className="btn btn-sm btn-danger">
                Delete
              </button>
            </div>

            <div className="video-footer">
              <small>Added: {formatDate(video.createdAt)}</small>
            </div>
          </div>
        ))}
      </div>

      {videos.length === 0 && (
        <div className="no-content">
          <h3>No video lessons added yet</h3>
          <p>Start by adding your first YouTube video lesson.</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary"
          >
            Add First Video
          </button>
        </div>
      )}

      {/* Add Video Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add Video Lesson</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-content">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="form-control"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="form-control"
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Subject *</label>
                  <select
                    value={formData.subjectId}
                    onChange={(e) => setFormData({...formData, subjectId: e.target.value})}
                    className="form-control"
                    required
                  >
                    <option value="">Select Subject</option>
                    <option value="mathematics">Mathematics</option>
                    <option value="english">English</option>
                    <option value="science">Science</option>
                    <option value="social_studies">Social Studies</option>
                    <option value="history">History</option>
                    <option value="geography">Geography</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Level *</label>
                  <select
                    value={formData.levelId}
                    onChange={(e) => setFormData({...formData, levelId: e.target.value})}
                    className="form-control"
                    required
                  >
                    <option value="">Select Level</option>
                    <option value="grade7">Grade 7</option>
                    <option value="form2">Form 2</option>
                    <option value="grade12">Grade 12</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>YouTube URL *</label>
                <input
                  type="url"
                  value={formData.videoUrl}
                  onChange={(e) => handleVideoUrlChange(e.target.value)}
                  className="form-control"
                  placeholder="https://www.youtube.com/watch?v=..."
                  required
                />
                <small>Enter the full YouTube video URL</small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Topic</label>
                  <input
                    type="text"
                    value={formData.topic}
                    onChange={(e) => setFormData({...formData, topic: e.target.value})}
                    className="form-control"
                    placeholder="e.g., Algebra, Photosynthesis, Essay Writing"
                  />
                </div>

                <div className="form-group">
                  <label>Duration (seconds)</label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})}
                    className="form-control"
                    placeholder="300"
                    min="1"
                  />
                </div>
              </div>

              {formData.thumbnailUrl && (
                <div className="thumbnail-preview">
                  <label>Thumbnail Preview</label>
                  <img 
                    src={formData.thumbnailUrl} 
                    alt="Thumbnail preview"
                    className="preview-image"
                  />
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  Add Video
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoManagement;
